export type PermissionId =
  | CompanyPermissionId
  | CompanyVariantScenarioPermissionId
  | CompanyGroupPermissionId
  // eslint-disable-next-line @typescript-eslint/no-duplicate-type-constituents
  | CompanyGroupSingleUserPermissionId
  | OrganizationPermissionId
  | PortfolioPermissionId
  | UserGroupPermissionId
  | ResourcelessPermissionId;

// Permissions that are tied to a company but not to a variant scenario
export enum CompanyPermissionId {
  TEMP = 'TEMP', // Placeholder to enforce that this is a string enum
}

export enum CompanyVariantScenarioPermissionId {
  READ_CSRD_TAILORINGS = 'READ_CSRD_TAILORINGS',
  EDIT_CSRD_TAILORINGS = 'EDIT_CSRD_TAILORINGS',
  // Grants a user group READ access to the climate-risk lens (physical +
  // transition sub-pages) for a company variant/scenario. Variant/scenario-scoped,
  // mirroring the CSRD tailoring privileges above; read-only (no EDIT counterpart).
  // It is the authoritative *entitlement* for both the climate result endpoints and
  // the lens's access-details slot: holding DMA/CSRD access does NOT open the climate
  // lens, because climate is sold separately (PLT-5831). It is not the whole gate —
  // read access to the owning company is required alongside it, since the two are
  // granted and revoked independently. Any new climate surface should call
  // `assertHasAccessToVariantClimateRisk`, which enforces both from a variant id;
  // this privilege on its own is half a gate. Scope note — this governs the lens, not
  // every climate-derived figure: per-hazard climate financial effects are also
  // emitted as native E1-cca risk rows in the DMA result set (PLT-5480), which stay
  // on CSRD-result access like the rest of the DMA. Auto-granted at publish for
  // investor-DD custom companies; otherwise granted per customer via the company
  // scenario-privileges admin toggle. PLT-5777. The lens is listed for every viewer,
  // and shown locked to one without this privilege (PLT-6944).
  READ_CLIMATE_RISK = 'READ_CLIMATE_RISK',
  // Grants a user group READ access to the Financial Effects lens for a
  // company variant/scenario, independently of DMA/CSRD-result access. The FE
  // and Climate-risk lenses each have their own switch so a customer can be
  // sold one without unlocking the full DMA lens (PLT-5790). Variant/scenario-
  // scoped and read-only, mirroring READ_CLIMATE_RISK above. FE is sold
  // separately and is OFF by default: a team user grants it per customer via
  // the company scenario-privileges admin toggle (on the company's
  // primary/canonical user group). The one automatic grant is for the custom
  // companies of an investor organisation holding
  // `USE_CUSTOM_COMPANY_FINANCIAL_EFFECTS`, which get it together with
  // CALCULATE_FINANCIAL_EFFECTS at publish and on every statement save
  // (PLT-7918). Team users bypass.
  READ_FINANCIAL_EFFECTS = 'READ_FINANCIAL_EFFECTS',
  // Grants a user group the right to TRIGGER a recompute of the Climate-risk /
  // Financial-effects datasets for a company variant/scenario, independently of
  // the READ_* privileges above (PLT-5790). Separating "may see the results"
  // (READ_*) from "may kick off a recalculation" (CALCULATE_*) lets a customer
  // be given read-only access without the ability to spend compute, or vice
  // versa. Variant/scenario-scoped; team users bypass. CALCULATE_FINANCIAL_EFFECTS
  // gates the FE recalc endpoint; CALCULATE_CLIMATE_RISK gates the site-locations
  // publish ("Update assessment"), which recomputes climate risk.
  CALCULATE_CLIMATE_RISK = 'CALCULATE_CLIMATE_RISK',
  CALCULATE_FINANCIAL_EFFECTS = 'CALCULATE_FINANCIAL_EFFECTS',
  // Gates the euro financial-effect figures emitted WITHIN the Climate lens
  // (company / transition / site results — physical climate-risk is
  // euro-quantified), independent of both READ_FINANCIAL_EFFECTS (the DMA/FE
  // lens) and READ_CLIMATE_RISK (climate access itself). This lets "monetized
  // climate" be sold without the DMA/FE lens: a climate-only customer sees the
  // climate risk verdicts but not the euro FE riders unless separately granted
  // this privilege. The effective gate is this privilege OR
  // READ_FINANCIAL_EFFECTS, so a full-FE customer sees climate euros
  // automatically. Off by default, no auto-grant, no backfill. Team users
  // bypass. (PLT-5790, Nordic Sustainability.)
  READ_CLIMATE_FINANCIAL_EFFECTS = 'READ_CLIMATE_FINANCIAL_EFFECTS',
}

export enum CompanyGroupPermissionId {
  TEMP = 'TEMP', // Placeholder to enforce that this is a string enum
}

// Permissions that can be given directly to a single user instead of a user group.
// Should be a subset of CompanyGroupPermissionId
export const companyGroupSingleUserPermissionIds = [CompanyGroupPermissionId.TEMP] as const;
export type CompanyGroupSingleUserPermissionId = (typeof companyGroupSingleUserPermissionIds)[number];

export enum PortfolioPermissionId {
  TEMP = 'TEMP', // Placeholder to enforce that this is a string enum
}

export enum ResourcelessPermissionId {
  // Grants members of the consumer user group access to the AI Chat web UI
  // (`/chat`). Team users always have access via the team-shortcut in
  // `assertUserHasResourcelessPrivilege`. For non-team users, granting this
  // privilege to a user group (typically the organization's organizational
  // user group) enables every member of that group. See chat handler in
  // `src/services/main/upserve/modules/chat/` and the `/chat` frontend gate
  // in `src/ui/views/internal/chat-view`.
  //
  // Distinct from `USE_AI_CHAT_CONNECTOR` below — that one gates the public
  // MCP + OAuth surface used by external clients (ChatGPT, Claude, Gemini).
  // The two are independent: an org may be enabled for web-only,
  // connector-only, or both, depending on what it actually needs.
  //
  // Modeled as RESOURCELESS rather than ORGANIZATION because chat access is
  // a feature the user can or cannot use — it is not "permission to act on a
  // specific organization's data". Organization-scoped privileges are about
  // operating on a specific organization's resources (managing its
  // subscription, creating companies in it, etc.); feature-access flags
  // belong on the resourceless axis instead.
  USE_AI_CHAT_WEB = 'USE_AI_CHAT_WEB',
  // Grants members of the consumer user group access to the Upright Platform
  // MCP connector — i.e. `POST /api/mcp` and the OAuth 2.1 consent flow at
  // `/mcp-authorize` (and the underlying `POST /api/mcp/authorize`). This is
  // the surface that ChatGPT, Claude, Gemini and other MCP clients hit when
  // a user adds Upright as a connector. Team users always have access via
  // the team-shortcut; for non-team users the privilege is granted explicitly
  // to a user group — via the privileges admin UI, or, for new DMA Freemium
  // orgs and DMA Consultant companies, by default at account creation alongside
  // `USE_AI_CHAT_WEB` (PLT-5250, see `manage-organizations/services.ts`).
  // Outside those onboarding flows the two privileges stay independent: we do
  // NOT auto-grant the connector alongside web, because the two surfaces have
  // different trust models (our UI + system prompt + rate limits vs. a
  // third-party client we don't control), so enabling one is never an implicit
  // grant of the other. See `mcp/access.ts` and `mcp/index.ts`.
  USE_AI_CHAT_CONNECTOR = 'USE_AI_CHAT_CONNECTOR',
  // Grants members of the consumer user group the EU data-residency option for
  // the web AI chat — i.e. the ability to opt a chat request onto the
  // `'bedrock-eu'` provider (Claude on AWS Bedrock in eu-west-1, via the
  // `eu.anthropic.*` inference profiles) instead of the default US Anthropic
  // API. Modeled RESOURCELESS for the same reason as `USE_AI_CHAT_WEB` above:
  // it is a feature the user can or cannot use, not authority over a specific
  // organization's data.
  //
  // DEFAULT-OFF and INDEPENDENT of `USE_AI_CHAT_WEB`: granted per user group
  // via the privileges admin UI to customers who require EU data residency.
  // A non-team user who holds it runs every chat on `'bedrock-eu'`; the grant
  // is the whole switch (PLT-7018). Team users pass via the team-shortcut, so
  // for them the grant cannot be the signal: they run on `'bedrock-eu'` only
  // when the staff EU testing switch in the chat debug panel asks for it via
  // the `provider` payload field (see `resolveChatProvider` in
  // `chat/llm-provider.ts`). PLT-5033 / PLT-5021.
  USE_AI_CHAT_EU_RESIDENCY = 'USE_AI_CHAT_EU_RESIDENCY',
  // Grants a user group the self-serve "create a fund from an uploaded /
  // pasted holdings list" chat feature — i.e. the staged fund-upload chat MCP
  // tools (`match_all_holdings` through `create_fund_from_staged`) that turn a
  // holdings list into a computed fund (`company_group` of type FUND) without
  // leaving the chat.
  // DEFAULT-OFF: team users always have it via the team-shortcut in
  // `assertUserHasResourcelessPrivilege`; for non-team users it is granted to a
  // user group via the privileges admin UI (see the user-group privileges
  // subview).
  //
  // Modeled RESOURCELESS rather than ORGANIZATION for the same reason as the
  // AI-chat flags above: it is a feature the user can or cannot use, not
  // authority over a specific organization's data. It covers the whole flow
  // including the finalize/persist step: no `CREATE_COMPANIES` privilege is
  // required, which is what lets fund upload be granted without also granting
  // custom/DD-company creation. Not sufficient on its own to reach the feature,
  // though — the chat surface itself is gated on `USE_AI_CHAT_WEB`.
  //
  // The write target is scoped by membership instead: a non-team caller may only
  // create the fund in an organization they belong to, while a team user may
  // target any. See `resolveAndAuthorizeOrganization` in
  // `mcp/tools/create-fund-from-holdings.ts`. PLT-5525 / PLT-5634.
  USE_FUND_UPLOAD = 'USE_FUND_UPLOAD',
  // Rollout kill-switch for the physical climate-risk lens on custom (DD /
  // CUSTOMER_SCENARIO) companies. It gates the site-identification side of that
  // feature so it can be turned on audience-by-audience without a deploy: the
  // automatic site-identification + persistence step that runs during the
  // custom-company publish pipeline (see `perform-publish-ipr-product-set.ts`),
  // and the Site locations columns on the Suppliers and Custom companies lists,
  // which count what that step found. The two columns read it off the
  // customer's own groups, with no team shortcut: the buyer company's linked
  // user group for Suppliers (PLT-6994), the organization's groups for Custom
  // companies (PLT-6971). For a supplier added by a customer user the step
  // reads the same buyer group (PLT-7037), so the Suppliers column and the
  // sites agree. A buyer in a DMA Freemium or DMA Consultant organization needs
  // no grant: supplier site identification is on there by default (PLT-7200,
  // `buyerHasCustomCompanyClimateRisk`). For any other custom company it checks the acting user. Team
  // users pass the step on the team shortcut in both cases, so a team member
  // creating a company or supplier on a customer's behalf still exercises the
  // full path, even where that customer's column stays hidden.
  //
  // It no longer gates the "Climate risk" rail entry: every viewer sees the
  // lens, locked unless they hold `READ_CLIMATE_RISK`, the variant/scenario
  // privilege that governs whether its results are readable for a given
  // company at all (PLT-6944). This resourceless flag is purely the
  // phased-rollout control: team users always pass via the team-shortcut in
  // `assertUserHasResourcelessPrivilege`; during rollout it is granted to
  // internal/pilot user groups via the privileges admin UI, and at GA the
  // check is removed. PLT-5776.
  USE_CUSTOM_COMPANY_CLIMATE_RISK = 'USE_CUSTOM_COMPANY_CLIMATE_RISK',
  // Managed-rollout gate for the segmented "Products & services" editor on the
  // DMA dashboard (PLT-5846) — the List / Hierarchy view toggle, the segment
  // hierarchy tree, and the explicit Edit / Save / Recompute paradigm that
  // replaces autosave-on-change on that page. Lets the editor ship to a limited
  // set of customers while minor bugs are shaken out, before it becomes the
  // default product editor for everyone.
  //
  // OFF by default for everyone. During the managed rollout it is switched on
  // per user group by hand from the privileges admin UI — the intended pilot
  // audience is the user groups of DMA Consultant and DMA Freemium
  // organizations, chosen a few at a time, rather than granted automatically by
  // org type. There is deliberately no account-creation grant and no bulk
  // backfill, so this privilege is only ever held by explicitly chosen groups.
  //
  // Team users always pass via the team-shortcut in
  // `assertUserHasResourcelessPrivilege`, but on the frontend team access is
  // instead driven by the internal `blanket.plgSegmentEditor` View setting so
  // the work-in-progress editor is not forced onto every internal dashboard —
  // see `useHasProductSegmentsAccess`.
  //
  // Modeled RESOURCELESS rather than tied to a company/variant because it gates
  // a UI feature the user can or cannot use, not authority over a specific
  // company's data. Frontend-only: the product-set save endpoint is shared with
  // the autosave path, so there is no separate backend assertion. Temporary —
  // removed at GA when the editor becomes the default.
  USE_PRODUCT_SEGMENTS = 'USE_PRODUCT_SEGMENTS',
  // Temporary team-only gate for the "Assessment horizon" selector on the climate
  // Physical and Transition tabs (PLT-5876) — the 1–20-year picker that re-solves
  // the financial effects over a horizon other than the one the variant's results
  // were computed at.
  //
  // OFF by default for everyone and deliberately granted to nobody: team users
  // bypass every privilege check, so this member alone is what makes the selector
  // team-only. It exists because the tabs already ship delivered results to
  // customers — without a gate the selector would appear for every customer
  // holding the FE entitlement the moment it deploys, on numbers they have
  // already been given, at horizons nobody has reviewed.
  //
  // The intended path is team-only now, then ON FOR EVERYONE once the remaining
  // work is done and tested (Jannica, Aug 2026) — deliberately not a
  // customer-by-customer rollout. So the exit is deleting this member and its two
  // check sites, never a sweep of per-group grants. The privileges-admin row is
  // only an escape hatch in case one customer should see it early; nothing depends
  // on it ever being used.
  //
  // Enforced on the SERVER, unlike `USE_PRODUCT_SEGMENTS` above, which is
  // frontend-only: the three climate read endpoints ignore a `timeHorizon` query
  // param from a caller without the privilege, so a shared or hand-edited url cannot
  // reach the re-solve. Ignoring rather than rejecting is deliberate — such a request
  // degrades to the persisted figures instead of failing the tab.
  //
  // The frontend's part is only to hide the control: both tabs send whatever horizon
  // is in the url and let the endpoint decide. Do not read the hidden selector as a
  // second line of defence — the server check is the whole of it.
  //
  // Modeled RESOURCELESS rather than variant-scoped for the same reason as
  // `USE_CUSTOM_COMPANY_CLIMATE_RISK`: it gates a feature the user can or cannot
  // use, not access to a particular company's data — which the existing
  // `READ_CLIMATE_RISK` / `READ_FINANCIAL_EFFECTS` privileges already govern.
  // Temporary: removed at GA, after which the euro-FE entitlement alone governs.
  USE_CLIMATE_ASSESSMENT_HORIZON = 'USE_CLIMATE_ASSESSMENT_HORIZON',
  // Opts an investor organisation into the unified shell (Unified UI Phase 1,
  // PLT-6530): the white top strip, the Primer-pane rail and the launcher
  // landing at `/`, instead of the dark header and the assets table. It gates
  // chrome only — which header, rail and home view render — and changes no data
  // access: everything reachable in the new shell is reachable in the old one.
  //
  // OFF by default for everyone. Customers are granted it per organisation from
  // the privileges admin UI, on the organisation's root user group so it
  // resolves to every user beneath it; rollback is revoking the grant. Team
  // users would pass via the team-shortcut in `assertUserHasResourcelessPrivilege`
  // unconditionally, so `useUnifiedShell` does not consult the privilege for
  // them: a team account gets the shell only through the team-only
  // `internalAdmin.unifiedShell` View setting (also a row in the start menu at
  // the bottom left). That is what lets the shell be reviewed on production
  // while every team account still sees, by default, exactly what a customer
  // that has not been opted in sees. Nothing on the server reads this
  // privilege: no endpoint is reachable only from the new shell, so there is
  // nothing to assert.
  //
  // Only meaningful for users whose front page is the investor one; a DMA
  // organisation keeps its dashboard whatever it holds — `useUnifiedShell`
  // applies that condition, not the privilege.
  //
  // Modeled RESOURCELESS for the same reason as the AI-chat flags above: it is
  // a feature the user can or cannot use, not authority over a specific
  // organisation's data. Temporary: removed once the unified shell is the only
  // investor shell, at which point `useUnifiedShell` and its consumers lose
  // their branch.
  USE_UNIFIED_SHELL = 'USE_UNIFIED_SHELL',
  // Grants a user group the generated scientific evidence in the DMA
  // "Scientific support" tab — the article selection produced by the
  // reasoning/article pipeline for a DMA impact, cached per company variant.
  //
  // An Enterprise feature. Granted automatically where an account becomes
  // Enterprise or is provisioned by Upright — an org put on an Enterprise
  // subscription from the internal org page, a consultant client company
  // completed at the Enterprise tier through the payment admin override, and the
  // staff groups of a consultant org Upright creates or converts — and revoked
  // again when an org or company leaves Enterprise the same way (PLT-6708, see
  // `materiality/scientific-evidence-grant-access.ts`). Never granted by
  // self-signup or Stripe checkout. The privileges admin UI remains the per-group
  // switch for anything outside those flows. It is the whole of the
  // customer-facing gate: an entitled group gets evidence for every company it
  // can already see, generated on first open. There is
  // deliberately no per-company approval step — the quality bar lives in the
  // pipeline (direction checks, source-quality filters, mechanism-fit ranking),
  // not in a human sign-off, so adding one would gate delivery on a review cycle
  // we have decided not to run (Jonas, Sep 2026).
  //
  // Because there is no such step, the generation is on demand and therefore
  // spends money on a customer's click: the `dma_impact_evidence_generation`
  // quota, not this privilege, is what bounds that. Granting this without that
  // quota in place is what turns a universe-sized customer into an unbounded
  // search bill. Pre-generating a company's evidence ahead of a delivery stays
  // available (`src/scripts/one-off/pregenerate-dma-impact-evidence.ts`) but is
  // an optional warm-up, not a precondition.
  //
  // Does NOT grant the "Scientific support" tab on its own terms only: the tab
  // is shown for this privilege OR the legacy `FeatureId.GISE_STATEMENTS`
  // feature, which is the older gate the pre-existing customer group still sits
  // on. The two are independent on purpose — the legacy group keeps seeing the
  // analyst-curated list exactly as before, without the generated evidence,
  // until that flag is retired (PLT-6139).
  //
  // Modeled RESOURCELESS rather than variant-scoped for the same reason as
  // `USE_CUSTOM_COMPANY_CLIMATE_RISK`: it gates a feature the user can or cannot
  // use, not access to a particular company's data — which the existing CSRD
  // result access already governs, and which the endpoint still asserts
  // separately. PLT-6352.
  USE_DMA_SCIENTIFIC_EVIDENCE = 'USE_DMA_SCIENTIFIC_EVIDENCE',
  // Makes every company variant created in a variant scenario owned by the consumer
  // user group use the new PAI GHG estimator — `SpecificationPointer.PAI_GHG_NEXT`,
  // i.e. the v2 estimator reading DB coefficients — instead of whatever target the
  // creating code path would otherwise have written. It is what "this customer has
  // been onboarded to the new PAI GHG algo" means in the data: their own customer
  // scenario and every DD-tool scenario spun up under the same group are covered,
  // present and future, without a deploy per customer (PLT-6265).
  //
  // READ DIFFERENTLY FROM EVERY OTHER PRIVILEGE IN THIS ENUM. The others answer "may
  // the acting user do X" and are checked with `getUserHasResourcelessPrivilege`,
  // which returns true for any team user. This one answers "is the customer owning
  // this data whitelisted", so it is checked against the scenario's
  // `canonicalUserGroupId` with `getUserGroupHasResourcelessPrivilege` — no team-user
  // shortcut and no parent-group expansion, both deliberate: the team-user shortcut
  // would force the new estimator onto every variant a team member creates for any
  // customer, and expansion would extend one customer's onboarding to sibling groups
  // under a shared parent. Grant it on the exact group that owns the scenarios.
  //
  // It decides what NEW variants declare, plus one edit: a DD product-set re-publish
  // moves that company's existing variants onto the new estimator. Otherwise granting
  // it leaves current variants alone (migrated case by case), and revoking it only
  // stops future writes from choosing the new estimator.
  //
  // Two limits of that, both deliberate. A pre-release re-clone of a deep-frozen,
  // tailored or DD variant carries the previous release's results forward instead of
  // computing new ones, so it carries the estimator forward too and does not upgrade;
  // see `keepPaiGHGEstimatorTarget` in the variant creation options. And this is no
  // guarantee that a hand-set target survives elsewhere: the pre-release re-clone of
  // an autopicked variant still flattens the target to the release default for a
  // group without this privilege, exactly as it did before this existed.
  //
  // Temporary: the end state is the new estimator for everyone, at which point the
  // member and its check sites are deleted rather than the grants swept.
  USE_PAI_GHG_NEXT = 'USE_PAI_GHG_NEXT',
  // Custom impact frameworks ("custom thematics") in the Upright Agent: the
  // custom framework tools in `mcp/tools/custom-impact-frameworks.ts`, in web
  // chat and MCP clients alike. The tools are hidden from a user without it and
  // each one asserts it again, since an MCP client can call a tool the listing
  // did not offer. Frameworks are shared within the user's organisation, so
  // grant it on an organisation's root group. Team users always have it, which
  // is the internal pilot (PLT-6906). Modeled RESOURCELESS for the same reason
  // as the AI-chat flags above. The user also needs AI chat access to reach it.
  USE_CUSTOM_IMPACT_FRAMEWORKS = 'USE_CUSTOM_IMPACT_FRAMEWORKS',
  // Rollout switch for creating custom (investor DD) companies in the Upright
  // Agent: the single-company creation tools in `mcp/tools/dd-company.ts`, the
  // website lookup and correction beside them, and building a product set from
  // uploaded files (`mcp/tools/dd-product-set-from-files.ts`). One switch for web
  // chat and MCP connectors alike, so a rollout is one grant rather than one per
  // surface; the connector is offered the website route only (`connector` on the
  // registry entries), because reading files into a product set relies on the
  // web chat's own upload handling. The batch tool stays team-only.
  //
  // It opens the flow, it does not authorise a write: every create, edit and
  // publish still asserts `CREATE_COMPANIES` on the owning organization and spends
  // that organization's custom company allowance, exactly as the wizard does.
  // The user also needs AI chat or connector access to reach it.
  //
  // OFF by default and granted by hand, per trial organization, on its root user
  // group. Team users always pass via the team-shortcut. Modeled RESOURCELESS for
  // the same reason as the AI-chat flags above. Temporary: removed when the flow
  // opens to every organization holding `CREATE_COMPANIES`. PLT-7017.
  USE_CHAT_COMPANY_CREATION = 'USE_CHAT_COMPANY_CREATION',
  // Financial effects for an investor organisation's custom (DD) companies, from the annual financial
  // statements its users add to them. An organisation holding it gets `READ_FINANCIAL_EFFECTS` and
  // `CALCULATE_FINANCIAL_EFFECTS` on each custom company's DD scenario, on its root group: at publish,
  // when the product-set publish job creates or updates the company's variants, and whenever a statement
  // is saved for the company, in the input page or in chat. The publish job also links the company's
  // latest saved statement to the variants it creates, so a statement saved before the first results
  // reaches them. See `custom-company-financial-effects-grant.ts`.
  //
  // Read like `USE_SELF_SERVE_ACCESS`: checked on the organisation's root group with
  // `getUserGroupHasResourcelessPrivilege`, so it is the organisation, not the acting user, that holds
  // it. A team user publishing or saving for a customer neither needs it nor stands in for it, and a
  // grant on any other group does nothing. Investor organisations only: a supplier, and the companies of
  // a DMA Freemium or DMA Consultant organisation, are left to the existing grants.
  //
  // OFF by default and granted by hand from the privileges admin UI, per investor organisation; turning
  // it on is followed by `grant-custom-company-financial-effects.ts`, which grants the rights to the
  // organisation's existing custom companies. Turning it off stops new grants and leaves the existing ones
  // until they are removed per company. Modeled RESOURCELESS for the same reason as the AI-chat flags
  // above. PLT-7902.
  USE_CUSTOM_COMPANY_FINANCIAL_EFFECTS = 'USE_CUSTOM_COMPANY_FINANCIAL_EFFECTS',
  // Opens access requests to an organisation's members: asking for a package, lens, feature or more
  // of a quota, seeing their requests' states and withdrawing an open one, through
  // `/api/organizations/{organizationId}/access/requests`. Checked on the organisation's root group
  // with `getUserGroupHasResourcelessPrivilege`, so it is the organisation, not the caller, that
  // holds it: those routes answer as not found (410) for an organisation without it. Team users pass and may read
  // any organisation's requests, to preview the page. A request grants nothing; Ops fulfils it
  // through the agreement commands.
  //
  // OFF by default and granted on each organisation's root group at its launch.
  USE_SELF_SERVE_ACCESS = 'USE_SELF_SERVE_ACCESS',
  // Opens member management to an organisation's admins (MANAGE_USERS on its root): its members,
  // member policies, selections and guests through `/api/organizations/{organizationId}/members` and
  // `/api/organizations/{organizationId}/access/commands|preview`. Checked on the organisation's root
  // group with `getUserGroupHasResourcelessPrivilege`, so it is the organisation, not the caller, that
  // holds it: those routes answer as not found (410) for an organisation without it. Team users pass
  // and may read any organisation's members, to preview the page; they change them through the team
  // routes.
  //
  // OFF by default and granted on each organisation's root group at its launch.
  USE_SELF_SERVE_MEMBERS = 'USE_SELF_SERVE_MEMBERS',
}

// Permissions for user group management / admin features
export enum UserGroupPermissionId {
  MANAGE_USERS = 'MANAGE_USERS',
}

export enum OrganizationPermissionId {
  MANAGE_SUBSCRIPTIONS = 'MANAGE_SUBSCRIPTIONS',
  CREATE_COMPANIES = 'CREATE_COMPANIES',
  // Lets a DMA Freemium or DMA Consultant organization assess its suppliers:
  // create custom companies for pre-onboarding screening, in chat or through
  // the structured form, and read their DMA results. Granted by default to every
  // such organization's root user group (PLT-7200, `grantDefaultSupplierAssessmentAccess`)
  // and removable per organization from the privileges admin UI; team users pass
  // via the team-shortcut in `assertUserHasPrivilegeToOrganization`.
  //
  // Granted per *(user group, organization)* pair, not per organization, and that
  // distinction is load-bearing: privileges resolve upward through the group tree,
  // so a grant on a DMA Consultant's org-level group reaches every client-level
  // user beneath it. There is deliberately no per-client granularity here — a
  // supplier's own relation to its client is what scopes a list, a quota or a read
  // to one client, and every consumer has to apply that scope itself. Scoping by
  // `resource_organizationId` alone is correct for DMA Freemium (one client per
  // organization) and crosses client boundaries for DMA Consultant.
  //
  // Holding the privilege is necessary but not sufficient: the organization must
  // also be a PLG one. `canAssessSuppliers` in
  // `upserve/modules/organizations/manage-organizations/supplier-eligibility.ts`
  // is the single predicate that combines the two — use it rather than checking
  // this privilege directly.
  //
  // Deliberately separate from `CREATE_COMPANIES` rather than reusing it. That
  // one is the investor due-diligence capability, granted to investor orgs and
  // asserted by the investor creation paths; an org may hold either without the
  // other, and collapsing them would make "can this org do supplier
  // assessments" unanswerable — which is the question the quota, the Suppliers
  // list and the creation gate all ask. PLT-5812.
  //
  // ORGANIZATION rather than RESOURCELESS for the same reason `CREATE_COMPANIES`
  // is: it is authority to act on a specific organization's resources, not a
  // feature the user can or cannot use.
  ASSESS_SUPPLIERS = 'ASSESS_SUPPLIERS',
}
